import { useLocalParticipant } from '@cometchat/calls-sdk-core';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Tile from '../tile/Tile.web';
import './DraggableTile.css';

interface DraggableTileProps {
  containerBounds: {
    width: number;
    height: number;
  };
  disableDrag?: boolean;
}

const SIDE_GAP = 10;

const DraggableTile: React.FC<DraggableTileProps> = ({
  containerBounds,
  disableDrag = true,
}) => {
  const localParticipant = useLocalParticipant();
  const tileRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: SIDE_GAP, y: SIDE_GAP });
  const [size, setSize] = useState({ width: 230, height: 130 }); // Default size
  const [isAnimating, setIsAnimating] = useState(false);
  const [resizeStartData, setResizeStartData] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [currentCorner, setCurrentCorner] = useState<
    'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  >('top-left');

  // Size constraints with 2:1 width-to-height ratio
  const minSize = { width: 200, height: 100 }; // 2:1 ratio
  const maxSize = { width: 400, height: 200 }; // 2:1 ratio
  const aspectRatio = 2; // Width is double the height

  // Determine which corner the tile is in and set resize handle accordingly
  const determineCornerAndHandle = useCallback(
    (pos: { x: number; y: number }) => {
      const { width: containerWidth, height: containerHeight } =
        containerBounds;
      const { width: tileWidth, height: tileHeight } = size;

      const centerX = containerWidth / 2;
      const centerY = containerHeight / 2;
      const tileCenterX = pos.x + tileWidth / 2;
      const tileCenterY = pos.y + tileHeight / 2;

      let corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

      if (tileCenterX < centerX && tileCenterY < centerY) {
        corner = 'top-left';
      } else if (tileCenterX >= centerX && tileCenterY < centerY) {
        corner = 'top-right';
      } else if (tileCenterX < centerX && tileCenterY >= centerY) {
        corner = 'bottom-left';
      } else {
        corner = 'bottom-right';
      }

      setCurrentCorner(corner);
      return corner;
    },
    [containerBounds, size],
  );

  // Snap to corners
  const snapToCorner = useCallback(
    (currentPos: { x: number; y: number }) => {
      const { width: containerWidth, height: containerHeight } =
        containerBounds;
      const { width: tileWidth, height: tileHeight } = size;

      console.log('Snap to corner called with:', {
        containerWidth,
        containerHeight,
        tileWidth,
        tileHeight,
      });

      const corners = [
        { x: SIDE_GAP, y: SIDE_GAP, name: 'top-left' }, // top-left
        {
          x: containerWidth - tileWidth - SIDE_GAP,
          y: SIDE_GAP,
          name: 'top-right',
        }, // top-right
        {
          x: SIDE_GAP,
          y: containerHeight - tileHeight - SIDE_GAP,
          name: 'bottom-left',
        }, // bottom-left
        {
          x: containerWidth - tileWidth - SIDE_GAP,
          y: containerHeight - tileHeight - SIDE_GAP,
          name: 'bottom-right',
        }, // bottom-right
      ];

      let closestCorner = corners[0];
      let minDistance = Infinity;

      corners.forEach(corner => {
        const distance = Math.sqrt(
          Math.pow(currentPos.x - corner.x, 2) +
            Math.pow(currentPos.y - corner.y, 2),
        );
        if (distance < minDistance) {
          minDistance = distance;
          closestCorner = corner;
        }
      });

      return { x: closestCorner.x, y: closestCorner.y };
    },
    [containerBounds, size],
  );

  // Update corner detection when position changes
  useEffect(() => {
    determineCornerAndHandle(position);
  }, [position, determineCornerAndHandle]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (
        disableDrag ||
        (e.target as HTMLElement).classList.contains(
          'cometchat-calls-draggable-tile-resize-handle',
        )
      ) {
        return; // Don't start dragging if disabled or clicking on resize handle
      }

      e.preventDefault();
      setIsDragging(true);
      const rect = tileRef.current!.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    },
    [disableDrag],
  );

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      setResizeStartData({
        x: e.clientX,
        y: e.clientY,
        width: size.width,
        height: size.height,
      });
    },
    [size],
  );

  // Mouse move handler
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && !isResizing) {
        // Get the container element to calculate relative coordinates
        const container = tileRef.current?.parentElement;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const newX = e.clientX - containerRect.left - dragOffset.x;
        const newY = e.clientY - containerRect.top - dragOffset.y;

        // Constrain within container bounds
        const constrainedX = Math.max(
          0,
          Math.min(newX, containerBounds.width - size.width),
        );
        const constrainedY = Math.max(
          0,
          Math.min(newY, containerBounds.height - size.height),
        );

        setPosition({ x: constrainedX, y: constrainedY });
      } else if (isResizing) {
        // Calculate new size based on mouse movement and current corner
        let deltaX = e.clientX - resizeStartData.x;
        let deltaY = e.clientY - resizeStartData.y;

        // Adjust delta based on which corner we're resizing from
        if (currentCorner === 'top-left') {
          // Handle is at bottom-right, so dragging down/right should expand
        } else if (currentCorner === 'top-right') {
          // Handle is at bottom-left, so dragging down/left should expand
          deltaX = -deltaX; // Invert X (left = positive)
        } else if (currentCorner === 'bottom-left') {
          // Handle is at top-right, so dragging up/right should expand
          deltaY = -deltaY; // Invert Y (up = positive)
        } else if (currentCorner === 'bottom-right') {
          // Handle is at top-left, so dragging up/left should expand
          deltaX = -deltaX; // Invert X (left = positive)
          deltaY = -deltaY; // Invert Y (up = positive)
        }

        // Calculate the effective delta - use the primary direction based on movement
        // Instead of Math.max, calculate based on the intended resize direction
        const effectiveDelta =
          Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;

        // Ensure we don't get negative sizes by using a minimum delta
        const finalDelta = Math.max(
          effectiveDelta,
          -(resizeStartData.width - minSize.width),
        );

        let newWidth = resizeStartData.width + finalDelta;

        // Calculate height maintaining 2:1 aspect ratio (width = 2 * height)
        let newHeight = newWidth / aspectRatio;

        // Apply size constraints
        if (newWidth < minSize.width) {
          newWidth = minSize.width;
          newHeight = minSize.height;
        } else if (newWidth > maxSize.width) {
          newWidth = maxSize.width;
          newHeight = maxSize.height;
        }

        // Check boundary constraints
        let maxAllowedWidth = containerBounds.width - 2 * SIDE_GAP;
        let maxAllowedHeight = containerBounds.height - SIDE_GAP;

        // Adjust based on position for different corners
        if (currentCorner === 'top-left') {
          maxAllowedWidth = containerBounds.width - position.x - SIDE_GAP;
          maxAllowedHeight = containerBounds.height - position.y - SIDE_GAP;
        } else if (currentCorner === 'top-right') {
          maxAllowedWidth = position.x + size.width - SIDE_GAP;
          maxAllowedHeight = containerBounds.height - position.y - SIDE_GAP;
        } else if (currentCorner === 'bottom-left') {
          maxAllowedWidth = containerBounds.width - position.x - SIDE_GAP;
          maxAllowedHeight = position.y + size.height - SIDE_GAP;
        } else if (currentCorner === 'bottom-right') {
          maxAllowedWidth = position.x + size.width - SIDE_GAP;
          maxAllowedHeight = position.y + size.height - SIDE_GAP;
        }

        if (newWidth > maxAllowedWidth) {
          newWidth = maxAllowedWidth;
          newHeight = newWidth / aspectRatio;
        }

        if (newHeight > maxAllowedHeight) {
          newHeight = maxAllowedHeight;
          newWidth = newHeight * aspectRatio;
        }

        // Adjust position for corners that resize from top/left edges
        const newPosition = { ...position };
        if (currentCorner === 'top-left') {
          // Handle at bottom-right: position stays same, size grows down/right
        } else if (currentCorner === 'top-right') {
          // Handle at bottom-left: adjust X position as width changes
          newPosition.x = position.x - (newWidth - size.width);
        } else if (currentCorner === 'bottom-left') {
          // Handle at top-right: adjust Y position as height changes
          newPosition.y = position.y - (newHeight - size.height);
        } else if (currentCorner === 'bottom-right') {
          // Handle at top-left: adjust both X and Y positions
          newPosition.x = position.x - (newWidth - size.width);
          newPosition.y = position.y - (newHeight - size.height);
        }

        // Ensure position stays within bounds
        newPosition.x = Math.max(
          0,
          Math.min(newPosition.x, containerBounds.width - newWidth),
        );
        newPosition.y = Math.max(
          0,
          Math.min(newPosition.y, containerBounds.height - newHeight),
        );

        setSize({ width: newWidth, height: newHeight });
        setPosition(newPosition);
      }
    };

    const handleMouseUp = () => {
      if (isDragging && !isResizing) {
        setIsAnimating(true);
        const snapPosition = snapToCorner(position);
        setPosition(snapPosition);

        setTimeout(() => {
          setIsAnimating(false);
        }, 300);
      }
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [
    isDragging,
    isResizing,
    dragOffset,
    position,
    size,
    snapToCorner,
    containerBounds,
    resizeStartData,
    currentCorner,
  ]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (
        disableDrag ||
        (e.target as HTMLElement).classList.contains(
          'cometchat-calls-draggable-tile-resize-handle',
        )
      ) {
        return;
      }

      e.preventDefault();
      setIsDragging(true);
      const rect = tileRef.current!.getBoundingClientRect();
      const touch = e.touches[0];
      setDragOffset({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      });
    },
    [disableDrag],
  );

  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging && !isResizing) {
        e.preventDefault();
        const touch = e.touches[0];

        // Get the container element to calculate relative coordinates
        const container = tileRef.current?.parentElement;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const newX = touch.clientX - containerRect.left - dragOffset.x;
        const newY = touch.clientY - containerRect.top - dragOffset.y;

        const constrainedX = Math.max(
          0,
          Math.min(newX, containerBounds.width - size.width),
        );
        const constrainedY = Math.max(
          0,
          Math.min(newY, containerBounds.height - size.height),
        );

        setPosition({ x: constrainedX, y: constrainedY });
      }
    };

    const handleTouchEnd = () => {
      if (isDragging && !isResizing) {
        setIsAnimating(true);
        const snapPosition = snapToCorner(position);
        setPosition(snapPosition);

        setTimeout(() => {
          setIsAnimating(false);
        }, 300);
      }
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [
    isDragging,
    isResizing,
    dragOffset,
    position,
    size,
    snapToCorner,
    containerBounds,
  ]);

  // Constrain position when container bounds change
  useEffect(() => {
    // Ensure the tile stays within bounds when container resizes
    const maxX = Math.max(0, containerBounds.width - size.width);
    const maxY = Math.max(0, containerBounds.height - size.height);

    let constrainedX = Math.max(0, Math.min(position.x, maxX));
    let constrainedY = Math.max(0, Math.min(position.y, maxY));

    // Always snap to nearest corner when container bounds change
    // Don't just constrain - actually snap to the closest corner
    const snapPosition = snapToCorner({ x: constrainedX, y: constrainedY });
    constrainedX = snapPosition.x;
    constrainedY = snapPosition.y;

    console.log('Container resize - snapping to corner:', {
      containerBounds,
      currentPosition: position,
      constrainedPosition: { x: constrainedX, y: constrainedY },
      snapPosition,
      size,
    });

    if (constrainedX !== position.x || constrainedY !== position.y) {
      setPosition({ x: constrainedX, y: constrainedY });
    }

    // Update corner detection with new position
    setTimeout(() => {
      determineCornerAndHandle({ x: constrainedX, y: constrainedY });
    }, 0);
  }, [
    containerBounds.width,
    containerBounds.height,
    size.width,
    size.height,
    snapToCorner,
  ]);

  // Get the resize handle class based on current corner
  const getResizeHandleClass = () => {
    switch (currentCorner) {
      case 'top-left':
        return 'cometchat-calls-draggable-tile-resize-handle--bottom-right';
      case 'top-right':
        return 'cometchat-calls-draggable-tile-resize-handle--bottom-left';
      case 'bottom-left':
        return 'cometchat-calls-draggable-tile-resize-handle--top-right';
      case 'bottom-right':
        return 'cometchat-calls-draggable-tile-resize-handle--top-left';
      default:
        return 'cometchat-calls-draggable-tile-resize-handle--bottom-right';
    }
  };

  return (
    <div
      ref={tileRef}
      className={clsx('cometchat-calls-draggable-tile', {
        'cometchat-calls-draggable-tile--dragging': isDragging,
        'cometchat-calls-draggable-tile--animating': isAnimating,
      })}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: 100,
        cursor: disableDrag ? 'default' : isDragging ? 'grabbing' : 'grab',
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}>
      <div className="cometchat-calls-draggable-tile-content">
        <Tile participant={localParticipant} />
      </div>

      {/* Dynamic resize handle based on corner position */}
      {!disableDrag && (
        <div
          className={clsx(
            'cometchat-calls-draggable-tile-resize-handle',
            getResizeHandleClass(),
          )}
          onMouseDown={handleResizeStart}
          onTouchStart={e => {
            e.preventDefault();
            e.stopPropagation();
            const touch = e.touches[0];
            setIsResizing(true);
            setResizeStartData({
              x: touch.clientX,
              y: touch.clientY,
              width: size.width,
              height: size.height,
            });
          }}
        />
      )}
    </div>
  );
};

export default DraggableTile;
